import {Observable, of, from} from 'rxjs';
import {concatMap, flatMap, delay} from 'rxjs/operators';
import {List} from 'immutable';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import * as RecurringPaymentActions from 'web-app/react/entities/recurring-payments/actions';
import * as AccountItemObservables from 'web-app/react/entities/account-items/epics';
import {replayOnTriggerObservable} from 'web-app/util/epics-helpers';
import {EntityTypes} from 'web-app/react/entities/global-search/model';
import * as GlobalSearchApi from 'web-app/react/entities/global-search/api';
import * as InvoiceApi from 'web-app/api/invoice-api';
import * as GlobalEventActions from 'web-app/react/global-event/actions';

import * as Api from './api';
import * as Actions from './actions';

const listenForUpdates = (action$, state$) => {
    const replayObservable = Observable.merge(
        action$.ofType(Actions.fetchInvoices.type),
        action$.ofType(Actions.fetchPayments.type),
    );
    const resetObservable = action$.ofType(Actions.showingTargetId.type);

    const triggerReplayObservable = Observable.merge(
        action$.ofType(AccountItemActions.createPaymentSuccess.type),
        action$.ofType(AccountItemActions.updatePaymentSuccess.type),
        action$.ofType(AccountItemActions.deletePaymentSuccess.type),
        action$.ofType(AccountItemActions.createInvoiceSuccess.type),
        action$.ofType(AccountItemActions.createInvoiceWithGraphQLSuccess.type),
        action$.ofType(Actions.bulkCancelInvoicesSuccess.type),
        action$.ofType(RecurringPaymentActions.denyPaymentSuccess.type),
    );

    const predicate = replayAction => {
        return state$.value.plans.billingActivityOverview.targetId === replayAction.payload.targetId;
    };

    return replayOnTriggerObservable(replayObservable, resetObservable, triggerReplayObservable, predicate).catch(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
    );
};

const fetchBillPayersForFilter = action$ =>
    action$
        .ofType(Actions.fetchBillPayersForFilter.type)
        .debounceTime(300)
        .mergeMap(({payload}) => {
            if (payload.term === '') {
                return Observable.of(Actions.fetchBillPayersForFilterAborted.action());
            }

            return GlobalSearchApi.search(
                payload.term,
                [EntityTypes.BillPayer],
                payload.institutionId ? [payload.institutionId] : [],
                payload.organizationId ? [payload.organizationId] : [],
            )
                .map(({searchItems}) => Actions.fetchBillPayersForFilterSuccess.action({searchItems}))
                .catch(e => Observable.of(Actions.fetchBillPayersForFilterFailed.action(e)));
        });

const fetchPayments = action$ =>
    action$.ofType(Actions.fetchPayments.type).mergeMap(({payload}) =>
        AccountItemObservables.fetchPaymentsForTargetObservable(
            payload.targetId,
            payload.offset,
            payload.limit,
            payload.from,
            payload.to,
            payload.confirmedFilter,
            payload.paymentMethod,
            payload.deposited,
            payload.transactionStatus,
            payload.createdAtFrom,
            payload.createdAtTo,
            payload.includeDeleted,
            payload.billPayerIds,
            payload.sortBy,
            payload.sortAscending,
        )
            .flatMap(({payload: {csv, payments, invoices, billPayers, total}}) => [
                BillPayerActions.fetchAllSuccess.action({
                    billPayers,
                }),
                AccountItemActions.fetchAllSuccess.action({
                    items: payments.concat(invoices),
                }),
                Actions.fetchPaymentsSuccess.action({
                    csv,
                    payments,
                    offset: payload.offset,
                    targetId: payload.targetId,
                    total,
                }),
            ])
            .catch(e => Observable.of(Actions.fetchPaymentsFailed.action(e))),
    );

const fetchInvoices = action$ =>
    action$.ofType(Actions.fetchInvoices.type).mergeMap(({payload}) =>
        AccountItemObservables.fetchInvoicesForTargetObservable(
            payload.targetId,
            payload.offset,
            payload.limit,
            payload.from,
            payload.to,
            payload.createdAtFrom,
            payload.createdAtTo,
            payload.billPayerIds,
        )
            .flatMap(({payload: {csv, invoices, behaviors, billPayers}}) => [
                BillPayerActions.fetchAllSuccess.action({
                    billPayers,
                }),
                AccountItemActions.fetchAllSuccess.action({
                    items: invoices,
                }),
                Actions.fetchInvoicesSuccess.action({
                    csv,
                    behaviors,
                    invoices,
                    offset: payload.offset,
                    targetId: payload.targetId,
                }),
            ])
            .catch(e => Observable.of(Actions.fetchInvoicesFailed.action(e))),
    );

const fetchSearchItems = action$ =>
    action$
        .ofType(Actions.fetchSearchItems.type)
        .debounceTime(300)
        .mergeMap(({payload}) => {
            if (payload.term === '') {
                return Observable.of(Actions.fetchSearchItemsSuccess.action({searchItems: List()}));
            }
            return GlobalSearchApi.search(
                payload.term,
                [EntityTypes.BillPayer, EntityTypes.Invoice],
                payload.institutionId ? [payload.institutionId] : [],
                payload.organizationId ? [payload.organizationId] : [],
            )
                .map(({searchItems}) => Actions.fetchSearchItemsSuccess.action({searchItems}))
                .catch(e => Observable.of(Actions.fetchSearchItemsFailed.action(e)));
        });

export const fetchDeposits = action$ =>
    action$.ofType(Actions.fetchDeposits.type).mergeMap(({payload}) =>
        Api.fetchDeposits(payload.institutionId)
            .map(response => Actions.fetchDepositsSuccess.action(response))
            .catch(e => Observable.of(Actions.fetchDepositsFailed.action(e))),
    );

export const fetchDirectDebitFiles = action$ =>
    action$.ofType(Actions.fetchDirectDebitFiles.type).mergeMap(({payload}) =>
        Api.fetchDirectDebitFiles(payload)
            .mergeMap(response =>
                Observable.merge(
                    Observable.of(Actions.fetchDirectDebitFilesSuccess.action(response)),
                    response.refreshIn
                        ? Observable.of(Actions.fetchDirectDebitFiles.action(payload)).delay(response.refreshIn * 1000)
                        : Observable.empty(),
                ),
            )
            .catch(e => Observable.of(Actions.fetchDirectDebitFilesFailed.action(e))),
    );

export const makeDirectDebitRun = action$ =>
    action$.ofType(Actions.makeDirectDebitRun.type).mergeMap(({payload}) =>
        Api.makeDirectDebitRun(payload)
            .flatMap(response => [
                Actions.makeDirectDebitRunSuccess.action(response),
                Actions.fetchDirectDebitFiles.action(payload),
            ])
            .catch(e => Observable.of(Actions.makeDirectDebitRunFailed.action(e))),
    );

export const makeDirectCreditRun = action$ => {
    return action$.ofType(Actions.makeDirectCreditRun.type).mergeMap(({payload}) =>
        Api.makeDirectCreditRun(payload)
            .flatMap(response => [
                Actions.makeDirectCreditRunSuccess.action(response),
                Actions.fetchDirectDebitFiles.action(payload),
            ])
            .catch(e => Observable.of(Actions.makeDirectCreditRunFailed.action(e))),
    );
};
const bulkCancelInvoices = action$ =>
    action$.ofType(Actions.bulkCancelInvoices.type).flatMap(({payload}) => {
        const {invoices, notify, creditNoteDate} = payload;

        // Sequentially cancel the invoices - the API doesn't support multiple cancel reuqests at a time
        const invoicesObservable = from(invoices).pipe(
            concatMap(invoice =>
                from(
                    InvoiceApi.cancelInvoice(
                        invoice.institutionId,
                        invoice.billPayerId,
                        invoice.invoiceId,
                        notify,
                        creditNoteDate,
                    ),
                ).pipe(
                    flatMap(() => {
                        return of(Actions.didCancelInvoice.action());
                    }),
                ),
            ),
        );

        // Delay the success actions a bit - otherwise the user will never see the final progress state update (e.g. 'Cancelled 5 of 5 invoice(s)')
        return invoicesObservable
            .concat([
                Actions.bulkCancelInvoicesSuccess.action(),
                GlobalEventActions.updateSuccess.action(
                    i18next.t('plans.debt.billingActivityOverview.bulkCancelSuccess', {
                        numberOfInvoices: invoices.size,
                    }),
                ),
            ])
            .pipe(delay(1000));
    });

export default combineEpics(
    listenForUpdates,
    fetchPayments,
    fetchInvoices,
    fetchSearchItems,
    fetchDeposits,
    fetchDirectDebitFiles,
    makeDirectDebitRun,
    makeDirectCreditRun,
    fetchBillPayersForFilter,
    bulkCancelInvoices,
);
